import { useCallback, useEffect, useMemo, useState } from 'react';
import Table from 'components/Table';
import {
  numberToCurrencyString,
  dateToLabel,
  numberToDisplayString,
} from 'further-ui/utils';
import { Box, Tooltip, styled } from '@mui/material';
import api from 'lib/trpcClient';
import { CompareArrows } from '@mui/icons-material';
import { createInvestmentEditRoute } from 'adminConstants/routes';
import { format } from 'date-fns';
import { round } from 'lodash';
import NewSharesAllocatedCell from './NewSharesAllocatedCell';
import ExitSharesSoldCell from './ExitSharesSoldCell';
import UploadShareConsiderationSheet from './UploadShareConsiderationSheet';
import {
  ConsiderationType,
  ShareConsiderationAllocationMethod,
} from 'further-types/exit';
import { ExitDetailsFormValues } from '../ExitDetailsForm';
import type { ShareConsideration } from '../types';

const TrancheCell = styled('span')(() => ({
  display: 'inline-block',
  minWidth: '7rem',
}));

const TooltipLink = styled('a')(({ theme }) => ({
  color: theme.palette.text.rootColor,
}));

const SummaryRow = styled('tr')(() => ({
  backgroundColor: '#FBFBFA',
  marginTop: 4,
}));

const SummaryCell = styled('td')(() => ({
  fontWeight: 'bold',
  padding: '12px 8px',
}));

type ShareConsiderationFields = {
  considerationType?: ConsiderationType;
  shareConsiderationCompanyId?: string;
  shareConsiderationShareClassId?: string;
  totalConsiderationShares?: number;
  shareConsiderationValuePerShare?: number;
  shareConsiderationValueTotal?: number;
  shareConsiderationEisEligible?: boolean;
  shareConsiderationAllocationMethod?: ShareConsiderationAllocationMethod;
  permitPartialShareAllocation?: boolean;
};

type ShareConsiderationItem = {
  shareholdingId: string;
  tranche?: { fundName?: string };
  sourceInvestmentId?: string;
  investor?: { fullName?: string };
  transferSourceInvestor?: { fullName?: string };
  transferDate?: string;
  investmentDate?: string;
  allocationDate?: string;
  sharesAllocatedForSale?: number;
  suggestedSharesAllocatedForSale?: number;
  noOfShares?: number;
  newSharesAllocated?: number;
  suggestedNewSharesAllocated?: number;
  shareConsiderationValuePerShare?: number;
  externalInvestmentId?: string | null;
};

type StagedShareConsiderationData = {
  shareholdingId: string;
  allocationDate?: string;
  shareClass: string;
  shareAllocationUploadId?: string;
  investorName?: string;
  tranche?: { _id: string; fundName?: string };
  noOfShares: number | string;
  sharesAllocatedForSale: number | null | undefined;
  sharePrice: number;
  accruedFeesCharged: number;
  externalInvestmentId?: string | null;
  transferDate?: string;
  sourceInvestmentId?: string;
  transferSourceInvestor?: { fullName: string };
  shareAllocationUploadLogId?: string;
  shareAllocationRoundingDiff?: number;
  totalSaleValue?: number;
  performanceFee: number;
  newSharesAllocated?: number;
  investmentDate?: string;
  shareConsiderationValuePerShare?: number;
  investor?: {
    fullName: string;
  };
};

type Props = {
  exitDetails: ExitDetailsFormValues;
  shareConsiderations: Array<ShareConsideration>;
  setShareConsiderations: React.Dispatch<
    React.SetStateAction<Array<ShareConsideration>>
  >;
  disableTable: boolean;
  isBeingEdited: boolean;
  exitId?: string;
  onUploadComplete?: () => void;
};

const transformStagedDataToShareConsideration = (
  data: Array<StagedShareConsiderationData | null>,
  allocationMethod: ShareConsiderationAllocationMethod,
): Array<ShareConsideration> => {
  return data
    .filter(
      (item): item is StagedShareConsiderationData =>
        item !== null &&
        typeof item === 'object' &&
        'shareholdingId' in item &&
        'shareClass' in item,
    )
    .map((sc) => {
      // If upload method is selected, set all newSharesAllocated to 0
      const newSharesAllocated =
        allocationMethod === ShareConsiderationAllocationMethod.Upload
          ? 0
          : sc.newSharesAllocated ?? 0;

      return {
        shareholdingId: sc.shareholdingId,
        shareClass: sc.shareClass,
        noOfShares:
          typeof sc.noOfShares === 'string'
            ? Number(sc.noOfShares)
            : sc.noOfShares ?? 0,
        sharesAllocatedForSale: sc.sharesAllocatedForSale ?? 0,
        sharePrice: sc.sharePrice,
        accruedFeesCharged: sc.accruedFeesCharged ?? 0,
        performanceFee: sc.performanceFee ?? 0,
        allocationDate: sc.allocationDate,
        shareAllocationUploadId: sc.shareAllocationUploadId,
        investorName: sc.investorName ?? '',
        tranche: sc.tranche,
        externalInvestmentId: sc.externalInvestmentId ?? null,
        transferDate: sc.transferDate,
        sourceInvestmentId: sc.sourceInvestmentId,
        transferSourceInvestor: sc.transferSourceInvestor,
        shareAllocationUploadLogId: sc.shareAllocationUploadLogId,
        shareAllocationRoundingDiff: sc.shareAllocationRoundingDiff,
        totalSaleValue: sc.totalSaleValue,
        newSharesAllocated,
        investmentDate: sc.investmentDate,
        shareConsiderationValuePerShare:
          sc.shareConsiderationValuePerShare ?? 0,
        investor: sc.investor,
        // store the original newSharesAllocated so we know
        // if the user has changed it via the input box
        suggestedNewSharesAllocated: newSharesAllocated,
        // store the original sharesAllocatedForSale so we know
        // if the user has changed it via the input box
        suggestedSharesAllocatedForSale: sc.sharesAllocatedForSale ?? 0,
      };
    });
};

const ReviewShareConsiderations: React.FC<Props> = ({
  exitDetails,
  shareConsiderations,
  setShareConsiderations,
  disableTable,
  isBeingEdited,
  exitId,
  onUploadComplete,
}) => {
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [stageForEditParams, setStageForEditParams] = useState<
    {
      exitId: string;
      exitDate: string;
      firmId: string;
      sharePrice: number;
      isReceivedValueSharePriceUnchanged: boolean;
    } & ShareConsiderationFields
  >({
    exitId: '',
    exitDate: '',
    firmId: '',
    sharePrice: 0,
    isReceivedValueSharePriceUnchanged: false,
  });
  const [stageForCreationParams, setStageForCreationParams] = useState<
    {
      exitDate: string;
      companyIds: Array<string>;
      firmId: string;
      sharePrice: number;
      sharesToBeSold: number;
      shareAllocationUploadIds: Array<string>;
      considerationType: ConsiderationType;
    } & Omit<ShareConsiderationFields, 'considerationType'>
  >({
    exitDate: '',
    companyIds: [],
    firmId: '',
    sharePrice: 0,
    sharesToBeSold: 0,
    shareAllocationUploadIds: [],
    considerationType: ConsiderationType.Share,
  });

  const stageForEdit = api.exit.stageForEdit.useQuery(stageForEditParams, {
    enabled: !!stageForEditParams.exitId && isBeingEdited,
  });

  const stageForCreation = api.exit.stageForCreation.useQuery(
    stageForCreationParams,
    {
      enabled:
        !!stageForCreationParams.firmId &&
        !isBeingEdited &&
        stageForCreationParams.considerationType === ConsiderationType.Share,
    },
  );

  // Fetch share consideration history when editing
  useEffect(() => {
    // Update shareConsiderations when stageForEdit.data changes
    if (stageForEdit.data && isBeingEdited) {
      const formattedInitialData = transformStagedDataToShareConsideration(
        stageForEdit.data,
        exitDetails.shareConsiderationAllocationMethod ??
          ShareConsiderationAllocationMethod.Automatic,
      );
      setInitialShareConsiderations(formattedInitialData);
    }
  }, [
    stageForEdit.data,
    isBeingEdited,
    exitDetails.shareConsiderationAllocationMethod,
  ]);

  useEffect(() => {
    // Update shareConsiderations when stageForCreation.data changes
    // Always update when stageForCreation.data is available and we're not editing
    if (stageForCreation.data && !isBeingEdited) {
      const formattedInitialData = stageForCreation.data?.map(
        (sc: ShareConsideration) => {
          // If upload method is selected, set all newSharesAllocated to 0
          const newSharesAllocated =
            exitDetails.shareConsiderationAllocationMethod ===
            ShareConsiderationAllocationMethod.Upload
              ? 0
              : sc.newSharesAllocated ?? 0;

          return {
            ...sc,
            newSharesAllocated,
            // store the original newSharesAllocated so we know
            // if the user has changed it via the input box
            suggestedNewSharesAllocated: newSharesAllocated,
            // store the original sharesAllocatedForSale so we know
            // if the user has changed it via the input box
            suggestedSharesAllocatedForSale:
              sc.suggestedSharesAllocatedForSale ??
              sc.sharesAllocatedForSale ??
              0,
          };
        },
      );
      setInitialShareConsiderations(formattedInitialData);
    }
  }, [
    stageForCreation.data,
    isBeingEdited,
    exitDetails.shareConsiderationAllocationMethod,
  ]);

  useEffect(() => {
    // fetch the investors with shareholdings and their share consideration allocations
    if (exitDetails && Object.keys(exitDetails).length > 0) {
      if (isBeingEdited && exitId) {
        setStageForEditParams({
          exitId,
          exitDate: format(exitDetails.exitDate, 'dd/MM/yyyy'),
          firmId: exitDetails.firmId,
          sharePrice: Number(exitDetails.sharePrice),
          isReceivedValueSharePriceUnchanged: true, // This will need to be compared with previousExitDetails if available
          considerationType: exitDetails.considerationType,
          shareConsiderationCompanyId: exitDetails.shareConsiderationCompanyId,
          shareConsiderationShareClassId:
            exitDetails.shareConsiderationShareClassId,
          totalConsiderationShares: exitDetails.totalConsiderationShares
            ? Number(exitDetails.totalConsiderationShares)
            : undefined,
          shareConsiderationValuePerShare:
            exitDetails.shareConsiderationValuePerShare
              ? Number(exitDetails.shareConsiderationValuePerShare)
              : undefined,
          shareConsiderationValueTotal: exitDetails.shareConsiderationValueTotal
            ? Number(exitDetails.shareConsiderationValueTotal)
            : undefined,
          shareConsiderationEisEligible:
            exitDetails.shareConsiderationEisEligible,
          shareConsiderationAllocationMethod:
            exitDetails.shareConsiderationAllocationMethod,
          permitPartialShareAllocation:
            exitDetails.permitPartialShareAllocation,
        });
      } else {
        setStageForCreationParams({
          exitDate: format(exitDetails.exitDate, 'dd/MM/yyyy'),
          companyIds: exitDetails.selectedShareClasses,
          firmId: exitDetails.firmId,
          sharePrice: Number(exitDetails.sharePrice),
          sharesToBeSold: Number(exitDetails.sharesToBeSold ?? 0),
          shareAllocationUploadIds: exitDetails.selectedAllocations ?? [],
          considerationType:
            exitDetails.considerationType ?? ConsiderationType.Cash,
          shareConsiderationCompanyId: exitDetails.shareConsiderationCompanyId,
          shareConsiderationShareClassId:
            exitDetails.shareConsiderationShareClassId,
          totalConsiderationShares: exitDetails.totalConsiderationShares
            ? Number(exitDetails.totalConsiderationShares)
            : undefined,
          shareConsiderationValuePerShare:
            exitDetails.shareConsiderationValuePerShare
              ? Number(exitDetails.shareConsiderationValuePerShare)
              : undefined,
          shareConsiderationValueTotal: exitDetails.shareConsiderationValueTotal
            ? Number(exitDetails.shareConsiderationValueTotal)
            : undefined,
          shareConsiderationEisEligible:
            exitDetails.shareConsiderationEisEligible,
          shareConsiderationAllocationMethod:
            exitDetails.shareConsiderationAllocationMethod,
          permitPartialShareAllocation:
            exitDetails.permitPartialShareAllocation,
        });
      }
    }
  }, [exitDetails, isBeingEdited, exitId]);

  const totals = useMemo(
    () =>
      shareConsiderations.reduce(
        (acc, sc) => {
          const sharesAllocatedForSale = sc.sharesAllocatedForSale ?? 0;
          const newSharesAllocated = sc.newSharesAllocated ?? 0;

          const totalSharesAllocatedForSale = Number(
            (acc.totalSharesAllocatedForSale + sharesAllocatedForSale).toFixed(
              10,
            ),
          );
          const totalNewSharesAllocated = Number(
            (acc.totalNewSharesAllocated + newSharesAllocated).toFixed(10),
          );

          return {
            totalSharesAllocatedForSale,
            totalNewSharesAllocated,
          };
        },
        { totalSharesAllocatedForSale: 0, totalNewSharesAllocated: 0 },
      ),
    [shareConsiderations],
  );

  const setInitialShareConsiderations = (input: Array<ShareConsideration>) => {
    setShareConsiderations(input);
  };

  const setOverride = useCallback(
    (
      shareholdingId: string,
      overrideProperties: Partial<ShareConsideration>,
    ) => {
      setShareConsiderations((prevState: Array<ShareConsideration>) => {
        return prevState.map((sc: ShareConsideration): ShareConsideration => {
          if (sc.shareholdingId === shareholdingId) {
            return {
              ...sc,
              ...overrideProperties,
            };
          }
          return sc;
        });
      });
    },
    [setShareConsiderations],
  );

  const columns = useMemo(
    () => [
      {
        label: 'Tranche',
        key: 'tranche',
        sort: false,
        render: (elm: ShareConsiderationItem) => (
          <TrancheCell>{elm?.tranche?.fundName ?? ''}</TrancheCell>
        ),
      },
      {
        label: 'Name',
        key: 'name',
        sort: false,
        render: (elm: ShareConsiderationItem) => {
          if (!elm.sourceInvestmentId) {
            return elm?.investor?.fullName ?? '';
          }

          const sourceInvestorName = elm?.transferSourceInvestor?.fullName;
          return (
            <Tooltip
              title={
                <div>
                  This investor is the recipient of a transfer from{' '}
                  <TooltipLink
                    href={createInvestmentEditRoute(elm?.sourceInvestmentId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {sourceInvestorName}
                  </TooltipLink>{' '}
                  on {dateToLabel(elm?.transferDate)}.
                </div>
              }
            >
              <Box alignItems="center" display="flex" gap={4}>
                {elm?.investor?.fullName ?? ''}
                <CompareArrows />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        label: 'Subscription date',
        key: 'subscriptionDate',
        sort: false,
        render: (elm: ShareConsiderationItem) =>
          elm.investmentDate ? dateToLabel(elm.investmentDate) : '-',
      },
      {
        label: 'Allocation date',
        key: 'allocationDate',
        sort: false,
        render: (elm: ShareConsiderationItem) =>
          elm.allocationDate ? dateToLabel(elm.allocationDate) : '-',
      },
      {
        label: 'Shares held',
        key: 'noOfShares',
        sort: false,
        render: (elm: ShareConsiderationItem) => {
          const shareConsideration = shareConsiderations.find(
            (sc) => sc.shareholdingId === elm.shareholdingId,
          );
          return numberToDisplayString(
            shareConsideration?.noOfShares ?? elm.noOfShares ?? 0,
            {
              maximumFractionDigits: 10,
            },
          );
        },
      },
      {
        label: 'Exit shares sold',
        key: 'sharesAllocatedForSale',
        sort: false,
        render: (elm: ShareConsiderationItem) =>
          isBeingEdited
            ? numberToDisplayString(elm.sharesAllocatedForSale ?? 0, {
                maximumFractionDigits: 10,
              })
            : (() => {
                const shareConsideration = shareConsiderations.find(
                  (sc) => sc.shareholdingId === elm.shareholdingId,
                );

                return (
                  <ExitSharesSoldCell
                    cell={{
                      shareholdingId: elm.shareholdingId,
                      sharesAllocatedForSale: elm.sharesAllocatedForSale ?? 0,
                      suggestedSharesAllocatedForSale:
                        elm.suggestedSharesAllocatedForSale ??
                        elm.sharesAllocatedForSale ??
                        0,
                      noOfShares:
                        shareConsideration?.noOfShares ?? elm.noOfShares ?? 0,
                    }}
                    setOverride={setOverride}
                    disableTable={disableTable}
                  />
                );
              })(),
      },
      {
        label: 'New shares allocated',
        key: 'newSharesAllocated',
        sort: false,
        render: (elm: ShareConsiderationItem) => (
          <NewSharesAllocatedCell
            cell={{
              shareholdingId: elm.shareholdingId,
              newSharesAllocated: elm.newSharesAllocated ?? 0,
              suggestedNewSharesAllocated: elm.suggestedNewSharesAllocated ?? 0,
            }}
            setOverride={setOverride}
            disableTable={disableTable}
          />
        ),
      },
      {
        label: 'Share value',
        key: 'shareValue',
        sort: false,
        render: (elm: ShareConsiderationItem) => {
          if (!elm.newSharesAllocated || elm.newSharesAllocated === 0) {
            return '-';
          }
          const totalValue = round(
            (elm.newSharesAllocated ?? 0) *
              (elm.shareConsiderationValuePerShare ?? 0),
            2,
          );
          return numberToCurrencyString(totalValue, {
            fallback: '-',
          });
        },
      },
      {
        label: 'External subscription ID',
        key: 'externalInvestmentId',
        sort: false,
        render: (elm: ShareConsiderationItem) =>
          elm.externalInvestmentId ?? '-',
      },
    ],
    [setOverride, disableTable, isBeingEdited, shareConsiderations],
  );

  const isUploadMethod =
    exitDetails.shareConsiderationAllocationMethod ===
    ShareConsiderationAllocationMethod.Upload;

  const handleUploadComplete = () => {
    setIsUploadComplete(true);
    onUploadComplete?.();
  };

  // Reset upload complete state when allocation method changes away from upload
  useEffect(() => {
    if (
      exitDetails.shareConsiderationAllocationMethod !==
      ShareConsiderationAllocationMethod.Upload
    ) {
      setIsUploadComplete(false);
    }
  }, [exitDetails.shareConsiderationAllocationMethod]);

  // Show upload component if upload method is selected, we have data, and upload not complete
  if (isUploadMethod && shareConsiderations.length > 0 && !isUploadComplete) {
    return (
      <UploadShareConsiderationSheet
        exitDetails={exitDetails}
        shareConsiderations={shareConsiderations}
        setShareConsiderations={setShareConsiderations}
        onTemplateReset={() => {
          // Reset upload state when template is reset
          setIsUploadComplete(false);
        }}
        onUploadComplete={handleUploadComplete}
      />
    );
  }

  // Show table for the current/new share consideration
  return (
    <>
      <Table
        columns={columns}
        tablebody={shareConsiderations}
        loading={
          !!(
            stageForCreation.isLoading ||
            stageForEdit.isLoading ||
            (!!(isBeingEdited ? stageForEdit : stageForCreation).data &&
              shareConsiderations.length === 0)
          )
        }
        emptyMessage="No share considerations found for this exit."
        variant="nohover"
        TableFooter={
          <tfoot>
            <SummaryRow>
              <SummaryCell align="right" colSpan={5}></SummaryCell>
              <SummaryCell align="right">
                {numberToDisplayString(totals.totalSharesAllocatedForSale, {
                  maximumFractionDigits: 10,
                })}
              </SummaryCell>
              <SummaryCell align="right" colSpan={2}>
                {numberToDisplayString(totals.totalNewSharesAllocated, {
                  maximumFractionDigits: 2,
                })}
              </SummaryCell>
              <SummaryCell align="right" colSpan={1}></SummaryCell>
            </SummaryRow>
          </tfoot>
        }
      />
    </>
  );
};

export default ReviewShareConsiderations;
